import { useAppDispatch, useAppSelector } from '../../redux';
import {
  documentsAreaCommonSliceActions,
  documentsListActions,
  dossierSliceActions,
  getRubriche,
  homeActions,
  isInformativeUsername,
  LoginState,
  myWalletSliceActions,
  pirboxActions,
  resetApiContextStatus,
  setContext,
  setRubrica,
  signatureAreaActions,
  signRoomSliceActions,
  UserState,
  walletCommonSliceActions,
} from '../../../store';
import { useCallback, useEffect, useState } from 'react';
import appRoutes from '../../../routing/app-routes';
import { Rubrica } from '../../../api/generated';
import { useAppNavigation } from '../../../utils/navigation.utils';

export interface RubrichePageLogicCallbacks {
  rubriche: Rubrica[];
  rubrica: any;
  rubricaLabel: string;
  setRubricaLabel: (label: string) => void;
  setRubricaValue: (value: string) => void;
  onSubmit: () => void;
  onChange: (value: any) => void;
  rubricaLogicEnabled: boolean;
}
export const useRubricheLogic = (): RubrichePageLogicCallbacks => {
  const { rubriche, codiceUtente }: LoginState = useAppSelector(
    (state) => state.features.login
  );

  const {
    fiduciaria,
    informativeMode,
    setContextStatus,
    codiceRubrica,
  }: UserState = useAppSelector((state) => state.data.user);

  const dispatch = useAppDispatch();
  const { appNavigate } = useAppNavigation();

  const [rubrica, setRubricaValue] = useState<string>(codiceRubrica);
  const [rubricaLabel, setRubricaLabel] = useState<string>('');

  useEffect(() => {
    setRubricaValue(codiceRubrica);
  }, [codiceRubrica]);

  useEffect(() => {
    setRubricaLabel(
      rubriche?.find((r) => r.codiceRubrica === rubrica)?.nominative ?? ''
    );
  }, [rubriche, rubrica]);

  // Informative users (FDN...) have no rubriche: the select is hidden and the API must not be called.
  // The check uses codiceUtente (the same value sent to the API) because at login informativeMode
  // is not set yet when fiduciaria and codiceUtente are already available.
  useEffect(() => {
    if (
      fiduciaria &&
      codiceUtente &&
      !informativeMode &&
      !isInformativeUsername(codiceUtente)
    )
      dispatch(getRubriche());
  }, [fiduciaria, dispatch, codiceUtente, informativeMode]);

  const onSubmit = useCallback(() => {
    dispatch(setRubrica(rubrica));
    dispatch(setContext(rubrica));
  }, [dispatch, rubrica]);

  const onChange = useCallback(
    (value: any) => {
      dispatch(setRubrica(value.value));
      dispatch(setContext(value.value));
    },
    [dispatch]
  );

  // Redux state partial reset actions
  const resetReduxState = () => {
    dispatch(homeActions.reset());
    dispatch(myWalletSliceActions.resetWalletState());
    dispatch(walletCommonSliceActions.resetWalletCommonState());
    dispatch(pirboxActions.resetPirBox());
    dispatch(signatureAreaActions.resetSignatureAreaState());
    dispatch(documentsAreaCommonSliceActions.resetWalletCommonState());
    dispatch(documentsListActions.reset());
    dispatch(dossierSliceActions.resetState());
    dispatch(signRoomSliceActions.resetState());
  };

  useEffect(() => {
    if (setContextStatus === 'succeeded') {
      dispatch(resetApiContextStatus());
      // Clear all the data in redux related to the previous context
      // to avoid showing wrong data after context change
      resetReduxState();
      // Navigate to home after context change, this will trigger a redirect to Dashboard page
      appNavigate(appRoutes.Dashboard, { replace: true });
    }
  }, [dispatch, appNavigate, setContextStatus]);

  return {
    rubriche,
    rubrica,
    rubricaLabel,
    setRubricaValue,
    setRubricaLabel,
    onSubmit,
    onChange,
    rubricaLogicEnabled: !informativeMode && fiduciaria,
  };
};
